import React from 'react';
import { BodyText } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { CopyableText, DataTable, DataTableProps } from '@cdaas/reusable-components-daas';
import { AdminProductVariant, Country, Material } from '@cdaas/api-clients/dist/product-service-openapi';

import { combineVariantName, getVariantMaterials } from '../../../utils/products';

interface MaterialCodeVariantDetailsProps {
  variant: AdminProductVariant;
  variantPropertyValues?: Record<number, string[]>;
  productName?: string;
  countryId: number;
  countries: Country[];
  showMaterialCode?: boolean;
}

const MaterialCodeVariantDetails = ({
  variant,
  variantPropertyValues,
  productName,
  countryId,
  countries,
  showMaterialCode = true,
}: MaterialCodeVariantDetailsProps) => {
  const { t } = useTranslation('productscreen');

  const formatProductName = (productName: string, formattedVariantName: string) => {
    if (formattedVariantName.length === 0) {
      return productName;
    }
    return `${productName} ${formattedVariantName}`;
  };

  const getMaterialCodeLabel = (materialCodes: Material[]): JSX.Element => {
    if (materialCodes.length === 1) {
      return <BodyText>{materialCodes[0].code}</BodyText>;
    } else if (materialCodes.length > 1) {
      return <BodyText color="red600">{t('materialCodes.duplicateCodes')}</BodyText>;
    } else {
      return <BodyText color="red600">{t('materialCodes.missingCode')}</BodyText>;
    }
  };

  const getRows = () => {
    const countryName = countries.find(country => country.id === countryId)?.name;
    const formattedVariantNames = (variantPropertyValues && combineVariantName(variantPropertyValues[variant.id])) || '';
    const formattedProductName = formatProductName(productName || '', formattedVariantNames);
    const materialCodes = getVariantMaterials(countryId, variant);

    const rows: DataTableProps['rows'] = [
      {
        rowType: 'md',
        labelItem: {
          label: t('materialCodes.product'),
        },
        dataItem: {
          data: (
            <CopyableText
              text={formattedProductName}
              translations={{ copyLinkLabel: t('materialCodes.copy'), copyMessage: t('materialCodes.copyMessage') }}
              textProps={{ size: 'md' }}
            />
          ),
        },
      },
      {
        rowType: 'md',
        labelItem: {
          label: t('materialCodes.variant'),
        },
        dataItem: {
          data: formattedVariantNames ? formattedVariantNames : '-',
        },
      },
      {
        rowType: 'md',
        labelItem: {
          label: t('materialCodes.sku'),
        },
        dataItem: {
          data: variant.sku,
        },
      },
      {
        rowType: 'md',
        labelItem: {
          label: t('materialCodes.country'),
        },
        dataItem: {
          data: countryName || '',
        },
      },
    ];

    if (showMaterialCode) {
      const materialCodeLabel = showMaterialCode && getMaterialCodeLabel(materialCodes);
      rows.push({
        rowType: 'md',
        labelItem: {
          label: t('materialCodes.materialCode'),
        },
        dataItem: {
          data: materialCodeLabel,
        },
      });
    }

    return rows;
  };

  return <DataTable tableStyle="strong" rows={getRows()} />;
};

export default MaterialCodeVariantDetails;
